import { useMutation } from '@apollo/client';

import { variableLinesValidator } from '../validation/validator';
import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import GetContractDetailsQuery from '@/graphql/contracts/getContractDetails.query.gql';
import GetPaginatedFilteredVariableContractLinesQuery from
  '@/graphql/contracts/getPaginatedFilteredVariableContractLines.query.gql';
import UpdateFullContractLineMutation from '@/graphql/contracts/updateFullContractLine.mutation.gql';
import { notificationService } from '@/services/notification/notification.client';
import { ALL_ITEMS } from '@/config/common.config';
import { VariableContractLineOutput } from '@/graphql/contracts/_generated_/contracts';
import { UpdateFullContractLine, UpdateFullContractLineVariables } from
  '@/graphql/contracts/_generated_/updateFullContractLine.mutation';
import { VARIABLE_TYPE } from '@/config/statuses.config';
import { triggerVariableRefresh } from '../components/ContractLinesGrid/variableLinesSelectionObservable';

interface ContractLinesInputFields {
  stockCode?: string;
  productId?: UUID;
  unitPrice?: number;
  billingType?: string;
  billingInterval?: string
  salesHandoverNumber?: string;
  minCharge?: number;
  changeReason?: {
    name: string;
    comment?: string
  },
  unitType?: string;
}

const VARIABLE_HALF_YEARLY = 'Half-Yearly';

export function useUpdateFullVariableContractLine(contractId: UUID) {
  const [_updateContractLine] = useMutation<UpdateFullContractLine, UpdateFullContractLineVariables>(
    UpdateFullContractLineMutation,
    {
      client: apolloClientContracts,
      onError: (err) => {
        notificationService.open({
          title: 'Error',
          text: err.message,
          type: 'danger'
        });
        apolloClientContracts.refetchQueries({
          include: [GetPaginatedFilteredVariableContractLinesQuery, GetContractDetailsQuery],
        });
        triggerVariableRefresh();
      },
      onCompleted: () => {
        triggerVariableRefresh();
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetContractDetailsQuery,
          variables: {
            contractId,
          }
        },
        {
          query: GetPaginatedFilteredVariableContractLinesQuery,
          variables: {
            filter: { headerPrimaryKey: { eq: contractId } },
            first: ALL_ITEMS
          }
        }
      ]
    },
  );

  const updateVariableLine = (cl: VariableContractLineOutput,
    changedField: ContractLinesInputFields, validationContext?: object) => {
    const isValidContractLine = variableLinesValidator.isValidContractLine(cl, validationContext);

    if (isValidContractLine) {
      _updateContractLine({
        variables: {
          productId: cl.productId,
          unitPrice: cl.unitPrice,
          billingType: VARIABLE_TYPE,
          billingInterval: cl.billingInterval === VARIABLE_HALF_YEARLY ? 'HalfYearly' : cl.billingInterval,
          changeReason: cl.statusChangeReasonName ?
            { name: cl.statusChangeReasonName, comment: cl.statusChangeComment } : null,
          salesHandoverNumber: cl.salesHandoverNumber,
          minCharge: cl.minCharge,
          contractID: contractId,
          contractLineID: cl.iD,
          ...changedField
        }
      });
    }
  };

  return updateVariableLine;
}
